import { Button, CircularProgress, Divider, List, ListItem, Paper, Typography } from '@mui/material';
import { MemreFlexBox } from 'components/core';
import React, { useState, useCallback, useEffect } from 'react';
import { getSuggestedDistractors } from 'utils/getSuggestedDistractors';
import { stripHtml } from 'services/utils/stripHtml';

interface SuggestedDistractorsProps {
  questionText: string;
  correctAnswers: string[];
  existingAnswers?: string[]; // All existing answers (both correct and incorrect)
  onAddDistractor: (distractor: string) => Promise<void>;
  autoGenerate?: boolean; // Whether to auto-generate distractors initially
}

export default function SuggestedDistractors(props: SuggestedDistractorsProps) {
  const { questionText, correctAnswers, existingAnswers = [], onAddDistractor, autoGenerate = true } = props;
  const [suggestedDistractors, setSuggestedDistractors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [initialGeneration, setInitialGeneration] = useState(false);

  /**
   * Generates suggested distractors based on the question and correct answers
   */
  const handleGenerateDistractors = useCallback(async () => {
    setInfoMessage(null);
    setError(null);
    if (!questionText || correctAnswers.length === 0) {
      setError('Please add a question and at least one correct answer first');
      return;
    }

    try {
      setIsLoading(true);

      // Strip HTML from question text and correct answers before sending to API
      const cleanQuestionText = stripHtml(questionText);
      const cleanCorrectAnswers = correctAnswers.map((answer) => stripHtml(answer));

      const distractors = await getSuggestedDistractors('suggestedDistractors', cleanQuestionText, cleanCorrectAnswers);

      // Get all existing answer texts for comparison
      const existingAnswerTexts = existingAnswers.map((answer) => normalizeText(answer));

      // Filter out suggested distractors that already exist as answers
      const filteredDistractors = distractors.filter(
        (distractor) => !existingAnswerTexts.includes(normalizeText(distractor))
      );

      if (filteredDistractors.length === 0 && distractors.length > 0) {
        setInfoMessage('All suggested distractors already exist as answers');
      }

      setSuggestedDistractors(filteredDistractors);
    } catch (error) {
      console.error('Error generating distractors', error);
      setError('Failed to generate suggested distractors. Please try again.');
      setInfoMessage(null);
    } finally {
      setIsLoading(false);
    }
  }, [questionText, correctAnswers, existingAnswers]);

  /**
   * Adds a suggested distractor
   */
  const handleAddDistractor = async (distractor: string) => {
    try {
      // Call the callback provided by the parent
      await onAddDistractor(distractor);

      // Remove the added distractor from the suggestions
      setSuggestedDistractors(suggestedDistractors.filter((d) => d !== distractor));
    } catch (error) {
      console.error('Error adding distractor', error);
    }
  };

  // Automatically generate distractors once when the component first loads (if autoGenerate is true)
  useEffect(() => {
    if (autoGenerate && !initialGeneration && questionText && questionText.length >= 10 && correctAnswers.length > 0) {
      handleGenerateDistractors();
      setInitialGeneration(true);
    }
  }, [autoGenerate, initialGeneration, questionText, correctAnswers.length, handleGenerateDistractors]);

  return (
    <MemreFlexBox direction="column" gap={2} sx={{ width: '100%', mt: 2 }}>
      <Button
        variant="outlined"
        color="primary"
        onClick={handleGenerateDistractors}
        disabled={isLoading || !questionText || correctAnswers.length === 0}
        startIcon={isLoading ? <CircularProgress size={16} /> : null}
      >
        {isLoading ? 'Generating...' : 'Generate Suggested Wrong Answers'}
      </Button>

      {/* Show loading indicator */}
      {isLoading && (
        <Typography variant="body2" color="textSecondary" sx={{ textAlign: 'center' }}>
          Generating suggestions...
        </Typography>
      )}

      {/* Show error messages */}
      {!isLoading && error && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}

      {/* Show informational message */}
      {!isLoading && infoMessage && (
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1, textAlign: 'center' }}>
          {infoMessage}
        </Typography>
      )}

      {/* Show distractors if we have any */}
      {!isLoading && suggestedDistractors.length > 0 && (
        <Paper
          variant="outlined"
          sx={{
            maxHeight: '300px',
            overflow: 'auto',
            width: '100%',
          }}
        >
          <List>
            {suggestedDistractors.map((distractor, index) => (
              <React.Fragment key={`${distractor}-${index}`}>
                <ListItem
                  disablePadding
                  sx={{
                    px: 2,
                    py: 1,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      cursor: 'pointer',
                    },
                  }}
                  onClick={() => handleAddDistractor(distractor)}
                >
                  <MemreFlexBox direction="row" align="center" gap={1}>
                    <Button variant="text" color="primary" size="small" sx={{ minWidth: 'auto', p: 0.5 }}>
                      +
                    </Button>
                    <Typography>{distractor}</Typography>
                  </MemreFlexBox>
                </ListItem>
                {index < suggestedDistractors.length - 1 && <Divider component="li" />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      )}
    </MemreFlexBox>
  );
}

// Helper function to normalize text for comparison
const normalizeText = (text: string): string => {
  if (!text) return '';

  return stripHtml(text).toLowerCase().trim();
};

